import { LaunchServiceOptions as JShellLaunchServiceOptions } from '@jarvis/shell-commons/dist/services/webServiceRouting/types';
import { LaunchServiceOptions as JWebLaunchServiceOptions } from '@jarvis/shell-commons/dist/services/JWeb/types';
import {
  ExternalOptionsType,
  InitializationMethod,
  MfeOptionsType,
  TenantSelectionStrategy,
  TenantSelectionStrategyRule
} from 'src/types/mfeProps';
import { Logger } from './Logger';
import { ErrorType } from 'src/types/error';

interface GetLaunchOptionsParams {
  manifestProperties?: ExternalOptionsType;
  shouldDefaultToSignUp?: boolean;
}

const CONFIG = {
  DEFAULT_MFE_OPTIONS: {
    isDemo: false,
    accountLandingPage: 'signin',
    initializationMethod: InitializationMethod.Navigation,
    localization: {
      country: '',
      language: ''
    }
  } as const,
  UTO_SERVICE_IDS: [
    'UserTenantOnboarding',
    'UserTenantOnboardingLocal',
    'openUrl'
  ] as readonly string[],
  JSHELL_SERVICE_ID: 'pces-jwv-user-tenant-onboarding-app',
  SERVICE_ROUTING_TIMEOUT: 5000,
  DEPRECATION_WARNINGS: {
    TENANT_SELECTION_STRATEGY_RULES:
      'DEPRECATION WARNING: tenantSelectionStrategyRules is deprecated and will be removed in a future version. ' +
      'The removal of tenantType from the API will make this feature non-functional.',
    PICK_FIRST_USING_RULES:
      'DEPRECATION WARNING: PickFirstUsingRules strategy is deprecated and will be removed in a future version. ' +
      'Please use ShowTenantSelector or UseLastTenant strategies instead.'
  }
} as const;

export const getLaunchOptions = async ({
  manifestProperties,
  shouldDefaultToSignUp
}: GetLaunchOptionsParams): Promise<MfeOptionsType> => {
  Logger.log('getLaunchOptions called with options:', {
    manifestProperties,
    shouldDefaultToSignUp
  });

  const mfeOptions = { ...CONFIG.DEFAULT_MFE_OPTIONS, ...manifestProperties };
  const queryParams = new URLSearchParams(window.location.search);
  const initMethod =
    (queryParams.get('from') as InitializationMethod) ||
    manifestProperties?.from;

  switch (initMethod) {
    case InitializationMethod.Navigation:
      return handleNavigationInit(mfeOptions, queryParams);

    case InitializationMethod.JWebServiceRouting:
      return handleJWebServiceRoutingInit(mfeOptions);

    case InitializationMethod.JShellServiceRouting:
      return handleJShellServiceRoutingInit(mfeOptions, shouldDefaultToSignUp);

    case InitializationMethod.Modal:
      return handleModalInit(mfeOptions);

    default:
      return handleLegacyInit(mfeOptions, queryParams, shouldDefaultToSignUp);
  }
};

const handleNavigationInit = (
  mfeOptions: MfeOptionsType,
  queryParams: URLSearchParams
): MfeOptionsType => {
  return {
    ...mfeOptions,
    ...processQueryParams(queryParams, InitializationMethod.Navigation)
  };
};

const handleJWebServiceRoutingInit = async (
  mfeOptions: MfeOptionsType,
  jwebOptions?: JWebLaunchServiceOptions | null
): Promise<MfeOptionsType> => {
  Logger.log('Entering JWeb Service Routing logic');

  const jwebResult = jwebOptions ?? (await getJWebServiceRoutingOptions());
  Logger.log('JWeb Service Routing Options:', {
    serviceOptions: jwebResult?.serviceOptions
  });

  const updatedOptions = {
    ...mfeOptions,
    initializationMethod: InitializationMethod.JWebServiceRouting
  };

  if (jwebOptions || isValidJWebService(jwebResult)) {
    Logger.log('JWeb Service Routing Options:', {
      serviceOptions: jwebResult.serviceOptions
    });
    return { ...updatedOptions, ...jwebResult.serviceOptions };
  }

  return updatedOptions;
};

const handleJShellServiceRoutingInit = async (
  mfeOptions: MfeOptionsType,
  shouldDefaultToSignUp?: boolean,
  jshellOptions?: JShellLaunchServiceOptions | null
): Promise<MfeOptionsType> => {
  Logger.log('Entering JShell Service Routing logic');

  const jshellResult =
    jshellOptions ?? (await getJShellServiceRoutingOptions());
  Logger.log('JShell service routing Options:', {
    serviceOptions: jshellResult?.serviceOptions
  });

  const updatedOptions = {
    ...mfeOptions,
    initializationMethod: InitializationMethod.JShellServiceRouting
  };

  if (jshellOptions || isValidJShellService(jshellResult)) {
    const { serviceOptions } = jshellResult;
    Logger.log('JShell service routing Options:', { serviceOptions });

    const accountLandingPage = getAccountLandingPage(
      serviceOptions,
      shouldDefaultToSignUp
    );
    const localization =
      serviceOptions?.onboardingContext?.sessionContext?.user?.localization ||
      mfeOptions.localization;

    const result = {
      ...updatedOptions,
      ...serviceOptions,
      localization,
      accountLandingPage
    };
    Logger.log('mfeOptions:', result);
    return result;
  }
  return updatedOptions;
};

const handleModalInit = (mfeOptions: MfeOptionsType): MfeOptionsType => {
  Logger.log('Entering Modal initialization method');
  return {
    ...mfeOptions,
    initializationMethod: InitializationMethod.Modal
  };
};

const handleLegacyInit = async (
  mfeOptions: MfeOptionsType,
  queryParams: URLSearchParams,
  shouldDefaultToSignUp?: boolean
): Promise<MfeOptionsType> => {
  const [jwebResult, jshellResult] = await Promise.allSettled([
    getJWebServiceRoutingOptions(),
    getJShellServiceRoutingOptions()
  ]);

  const hasQueryParams = queryParams.size > 0;

  Logger.log('isQueryParameters: ', hasQueryParams);

  const isJWebServiceRouting =
    jwebResult.status === 'fulfilled' && isValidJWebService(jwebResult.value);

  const isJShellServiceRouting =
    jshellResult.status === 'fulfilled' &&
    isValidJShellService(jshellResult.value);

  if (isJShellServiceRouting) {
    return handleJShellServiceRoutingInit(
      mfeOptions,
      shouldDefaultToSignUp,
      jshellResult.value
    );
  }

  if (isJWebServiceRouting) {
    return handleJWebServiceRoutingInit(mfeOptions, jwebResult.value);
  }

  if (hasQueryParams) {
    let initializationMethod = InitializationMethod.Navigation;
    const errorType = queryParams.get('error_type');

    if (errorType === ErrorType.UserCanceled) {
      if (isJShellServiceRouting) {
        initializationMethod = InitializationMethod.JShellServiceRouting;
      } else if (isJWebServiceRouting) {
        initializationMethod = InitializationMethod.JWebServiceRouting;
      }
    }

    const result = {
      ...mfeOptions,
      ...processQueryParams(queryParams, initializationMethod)
    };
    Logger.log('mfeOptions:', result);
    return result;
  }

  Logger.log('mfeOptions:', mfeOptions);
  return mfeOptions;
};

const processQueryParams = (
  queryParams: URLSearchParams,
  initMethod: InitializationMethod
): Partial<MfeOptionsType> => {
  Logger.log('Entering Query Parameters logic');

  const tenantSelectionStrategy = queryParams.get(
    'tenantSelectionStrategy'
  ) as TenantSelectionStrategy;
  let tenantSelectionStrategyRules: TenantSelectionStrategyRule[] | null = null;

  const encodedRules = queryParams.get('tenantSelectionStrategyRules');
  if (encodedRules) {
    Logger.warn(CONFIG.DEPRECATION_WARNINGS.TENANT_SELECTION_STRATEGY_RULES);
    try {
      tenantSelectionStrategyRules = JSON.parse(atob(encodedRules));
    } catch (error) {
      Logger.error(`Error decoding TenantSelectionStrategyRules: ${error}`);
    }
  }

  if (tenantSelectionStrategy === TenantSelectionStrategy.PickFirstUsingRules) {
    Logger.warn(CONFIG.DEPRECATION_WARNINGS.PICK_FIRST_USING_RULES);
  }

  Logger.log('TenantSelectionStrategy: ', tenantSelectionStrategy);
  Logger.log(tenantSelectionStrategyRules ?? 'No rules');

  const result: Partial<MfeOptionsType> = {
    initializationMethod: initMethod,
    tenantSelectionStrategy,
    tenantSelectionStrategyRules
  };

  const country = queryParams.get('country');
  const language = queryParams.get('language');
  if (country && language) {
    result.localization = { country, language };
  }

  const accountLandingPage = queryParams.get('accountLandingPage');
  if (accountLandingPage) {
    result.accountLandingPage = accountLandingPage;
  }

  const propositionScreen = queryParams.get('propositionScreen');
  if (propositionScreen) {
    Logger.debug('Proposition Screen:', propositionScreen);
    result.propositionScreen = propositionScreen;
  }

  const forceLogin = queryParams.get('forceLogin');
  result.forceLogin = forceLogin === 'true';

  return result;
};

export const getJShellServiceRoutingOptions =
  async (): Promise<JShellLaunchServiceOptions | null> => {
    return createServiceRoutingRequest(
      () => window.Shell.v1.serviceRouting.getServiceInstanceLaunchOptions(),
      'JShell'
    ) as Promise<JShellLaunchServiceOptions | null>;
  };

export const getJWebServiceRoutingOptions =
  async (): Promise<JWebLaunchServiceOptions | null> => {
    return createServiceRoutingRequest(
      () =>
        window.JWeb.Plugins.ServiceRouting.getServiceInstanceLaunchOptions(),
      'JWeb'
    ) as Promise<JWebLaunchServiceOptions | null>;
  };

const createServiceRoutingRequest = async (
  serviceCall: () => Promise<unknown>,
  serviceName: string
): Promise<unknown> => {
  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => {
      Logger.log(
        `Timeout reached while getting ${serviceName} service routing options`
      );
      resolve(null);
    }, CONFIG.SERVICE_ROUTING_TIMEOUT);
  });

  try {
    const result = await Promise.race([serviceCall(), timeoutPromise]);
    return result;
  } catch (error) {
    Logger.log(
      `Error getting ${serviceName} service routing options: ${error}`
    );
    return null;
  }
};

const isValidJWebService = (jwebResult: JWebLaunchServiceOptions | null) =>
  jwebResult && CONFIG.UTO_SERVICE_IDS.includes(jwebResult.serviceId);

const isValidJShellService = (
  jshellResult: JShellLaunchServiceOptions | null
) => jshellResult && jshellResult.serviceId === CONFIG.JSHELL_SERVICE_ID;

export const getAccountLandingPage = (
  serviceOptions?: Record<string, unknown>,
  shouldDefaultToSignUp?: boolean
): string => {
  const onboardingContext = serviceOptions?.onboardingContext as {
    sessionContext?: {
      user?: {
        selectedLoginType?: string;
      };
    };
  };

  const selectedLoginType =
    onboardingContext?.sessionContext?.user?.selectedLoginType;

  if (!selectedLoginType) {
    return shouldDefaultToSignUp ? 'signup' : 'signin';
  }

  return selectedLoginType === 'createAccount' ? 'signup' : 'signin';
};
